// public/js/appSettings.js

/**
 * Application Settings Configuration
 * Contains default settings for filtering behavior per entity type
 * Later will be replaced with user-specific settings from database
 */

const AppSettings = {
    // Filter behavior settings per entity type
    // ALL modules use realtime filtering (no Apply button) as per Issue #62
    filterSettings: {
        // Dogs: Simple dataset, quick search/filtering
        dog: {
            realtime: true,      // Filter as user types/selects
            persistent: false,   // Don't persist filters on page reload
            autoApply: false     // Not needed for realtime mode
        },

        // Activities: Real-time multi-criteria filtering
        activity: {
            realtime: true,      // Filter as user types/selects
            persistent: false,   // Don't persist filters on page reload
            autoApply: false     // Not needed for realtime mode
        },

        // Files: Real-time filtering with immediate feedback
        file: {
            realtime: true,      // Filter as user types
            persistent: false,   // Don't persist filters on page reload
            autoApply: false     // Not needed for realtime mode
        },

        // Results: Real-time filtering
        result: {
            realtime: true,      // Filter as user types/selects
            persistent: false,   // Don't persist filters on page reload
            autoApply: false     // Not needed for realtime mode
        },

        // Notes: Real-time filtering
        note: {
            realtime: true,      // Filter as user types/selects
            persistent: false,   // Don't persist filters on page reload
            autoApply: false     // Not needed for realtime mode
        },

        // Weights: Real-time filtering
        weight: {
            realtime: true,      // Filter as user types/selects
            persistent: false,   // Don't persist filters on page reload
            autoApply: false     // Not needed for realtime mode
        },

        // Parcours: Real-time filtering
        parcour: {
            realtime: true,      // Filter as user types/selects
            persistent: false,   // Don't persist filters on page reload
            autoApply: false     // Not needed for realtime mode
        },

        // Users: Real-time filtering
        user: {
            realtime: true,      // Filter as user types/selects
            persistent: false,   // Don't persist filters on page reload
            autoApply: false     // Not needed for realtime mode
        },

        // Default fallback for any new entity types
        default: {
            realtime: true,      // Enable realtime filtering by default
            persistent: false,   // Don't persist filters by default
            autoApply: false     // No auto-apply needed for realtime mode
        }

    },

    // UI behavior settings
    ui: {
        // Filter indicator settings
        showActiveFilterCount: true,    // Show "3 active filters"
        showActiveFilterValues: true,   // Show "Type: Training, Status: Completed"
        highlightActiveFields: true,    // Highlight form fields with active filters

        // Apply button settings
        applyButtonText: 'Apply Filters',
        clearButtonText: 'Clear',

        // Filter debounce for real-time filtering (ms)
        realtimeDebounce: 300,

        // Animation settings
        filterTransitionDuration: 200   // ms for filter UI transitions
    },

    // Performance settings
    performance: {
        // Maximum items to filter before showing performance warning
        maxItemsForClientFiltering: 1000,
        
        // Debounce time for search input in ms
        searchDebounce: 300,
        
        // Cache filter results for performance
        cacheFilterResults: true
    }

};

/**
 * Get filter settings for a specific entity type
 * @param {string} entityType - Type of entity (e.g., 'activity', 'dog', 'file')
 * @returns {Object} Filter settings for the entity type
 */
AppSettings.getFilterSettings = function(entityType) {
    return this.filterSettings[entityType] || this.filterSettings.default;
};

/**
 * Check if real-time filtering is enabled for entity type
 * @param {string} entityType - Type of entity
 * @returns {boolean} True if real-time filtering enabled
 */
AppSettings.isRealtimeEnabled = function(entityType) {
    return this.getFilterSettings(entityType).realtime;
};

/**
 * Check if persistent filtering is enabled for entity type
 * @param {string} entityType - Type of entity
 * @returns {boolean} True if persistent filtering enabled
 */
AppSettings.isPersistentEnabled = function(entityType) {
    return this.getFilterSettings(entityType).persistent;
};

/**
 * Check if auto-apply is enabled for entity type
 * @param {string} entityType - Type of entity
 * @returns {boolean} True if auto-apply enabled
 */
AppSettings.isAutoApplyEnabled = function(entityType) {
    return this.getFilterSettings(entityType).autoApply;
};

/**
 * Update filter settings for an entity type (for future settings UI)
 * @param {string} entityType - Type of entity
 * @param {Object} newSettings - New settings to merge
 */
AppSettings.updateFilterSettings = function(entityType, newSettings) {
    if (!this.filterSettings[entityType]) {
        this.filterSettings[entityType] = { ...this.filterSettings.default };
    }
    
    Object.assign(this.filterSettings[entityType], newSettings);
    
    // Trigger settings change event for any listeners
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('appSettingsChanged', {
            detail: { entityType, newSettings }
        }));
    }
};

/**
 * Reset filter settings for an entity type to defaults
 * @param {string} entityType - Type of entity
 */
AppSettings.resetFilterSettings = function(entityType) {
    if (this.filterSettings[entityType]) {
        this.filterSettings[entityType] = { ...this.filterSettings.default };
    }
};

/**
 * Get all current filter settings (for debugging/settings UI)
 * @returns {Object} All current filter settings
 */
AppSettings.getAllSettings = function() {
    return {
        filterSettings: { ...this.filterSettings },
        ui: { ...this.ui },
        performance: { ...this.performance }
    };
};

// Make available globally for debugging
if (typeof window !== 'undefined') {
    window.AppSettings = AppSettings;
}

export default AppSettings;